import type { ParseResult } from './manifest/parse.js';

/**
 * Configuration comes from the environment at build time. There are no production
 * defaults: no API host, no CDN host, and no cache capacity. A missing value fails
 * loudly at boot rather than shipping a wrong default to 250 screens.
 */
export interface PlayerConfig {
  /** Base URL of the device API, e.g. https://api.example/. No default. */
  readonly apiBaseUrl: string;
  /** Reported in the heartbeat (technical-design §6.6) and used for the update check. */
  readonly appVersion: string;
  /**
   * Cache ceiling used before the first manifest arrives; `settings.cache_max_bytes`
   * takes over once a manifest is in hand (technical-design §11.2, §11.5). This is a
   * capacity number: it is deliberately unset by default and fails loudly.
   */
  readonly cacheMaxBytesBootstrap: number;
  /**
   * House content asset. decision-log O131 (who supplies house content and how it
   * reaches the cache before the first manifest) is open, so this is optional: with
   * it unset the player renders the built-in branded card, which is still never
   * black and never an error (invariant 9).
   */
  readonly houseContentUrl: string | null;
}

/**
 * Error reporting for the bundle (dwf-007 §7.8; decision-log D30 makes the DSN configuration
 * each bundle reads). Deliberately *not* part of `PlayerConfig`: nothing the player does
 * depends on it, and it must not share `PlayerConfig`'s fate. `loadConfig` refuses to boot on
 * a missing required value; this cannot, because a screen that stops playing over an
 * unconfigured error reporter is the failure invariant 9 exists to prevent.
 *
 * Both values are optional and both are read the same way, so the half-configured case —
 * a DSN with no environment tag — reaches `@dw/observability`, which refuses to send untagged
 * events and says so on the console. `release-player.yml` rejects that pair before a bundle is
 * built, which is where the mistake is actually catchable.
 */
export interface SentryBundleConfig {
  readonly dsn: string | null;
  readonly environment: string | null;
}

export const CONFIG_KEYS = {
  apiBaseUrl: 'VITE_DW_API_BASE_URL',
  appVersion: 'VITE_DW_APP_VERSION',
  cacheMaxBytesBootstrap: 'VITE_DW_CACHE_MAX_BYTES_BOOTSTRAP',
  houseContentUrl: 'VITE_DW_HOUSE_CONTENT_URL',
  sentryDsn: 'VITE_DW_SENTRY_DSN',
  sentryEnvironment: 'VITE_DW_SENTRY_ENVIRONMENT',
} as const;

export function loadConfig(env: Record<string, string | undefined>): ParseResult<PlayerConfig> {
  const errors: string[] = [];

  const apiBaseUrl = required(env, CONFIG_KEYS.apiBaseUrl, errors);
  if (apiBaseUrl !== null && !/^https?:\/\//.test(apiBaseUrl)) {
    errors.push(`${CONFIG_KEYS.apiBaseUrl}: expected an absolute http(s) URL`);
  }

  const appVersion = required(env, CONFIG_KEYS.appVersion, errors);

  const rawCacheMax = required(env, CONFIG_KEYS.cacheMaxBytesBootstrap, errors);
  let cacheMaxBytesBootstrap: number | null = null;
  if (rawCacheMax !== null) {
    const parsed = Number(rawCacheMax);
    if (!Number.isSafeInteger(parsed) || parsed <= 0) {
      errors.push(`${CONFIG_KEYS.cacheMaxBytesBootstrap}: expected a positive integer byte count`);
    } else {
      cacheMaxBytesBootstrap = parsed;
    }
  }

  const houseContentUrl = optional(env, CONFIG_KEYS.houseContentUrl);

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    value: {
      apiBaseUrl: (apiBaseUrl as string).replace(/\/+$/, ''),
      appVersion: appVersion as string,
      cacheMaxBytesBootstrap: cacheMaxBytesBootstrap as number,
      houseContentUrl,
    },
  };
}

/** Reads independently of `loadConfig`, so a bundle with a broken config can still report it. */
export function loadSentryConfig(env: Record<string, string | undefined>): SentryBundleConfig {
  return {
    dsn: optional(env, CONFIG_KEYS.sentryDsn),
    environment: optional(env, CONFIG_KEYS.sentryEnvironment),
  };
}

function required(
  env: Record<string, string | undefined>,
  key: string,
  errors: string[],
): string | null {
  const value = env[key]?.trim();
  if (value === undefined || value.length === 0) {
    errors.push(`${key} is not set and has no default`);
    return null;
  }
  return value;
}

/** Absent and blank are the same thing: Vite inlines an unset key as `undefined`. */
function optional(env: Record<string, string | undefined>, key: string): string | null {
  const value = env[key]?.trim() ?? '';
  return value.length > 0 ? value : null;
}
